import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, finalize } from 'rxjs';
import { AuthService } from './auth.service';
import { CrossTabSyncService } from './cross-tab-sync.service';

@Injectable({ providedIn: 'root' })
export class SessionExpiryWatcherService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly crossTabSync = inject(CrossTabSyncService);

  private tokenTimerId: ReturnType<typeof setTimeout> | null = null;
  private tokenChangesSub?: Subscription;
  private crossTabEventsSub?: Subscription;
  private started = false;
  private refreshInProgress = false;

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.tokenChangesSub = this.authService.accessTokenChanges.subscribe(() => {
      this.rescheduleFromCurrentToken();
    });
    this.crossTabEventsSub = this.crossTabSync.events$.subscribe((event) => {
      if (event.eventType === 'AUTH_LOGIN'
        || event.eventType === 'AUTH_LOGOUT'
        || event.eventType === 'AUTH_TOKEN_UPDATED') {
        this.rescheduleFromCurrentToken();
      }
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.onWindowFocus);

    this.rescheduleFromCurrentToken();
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.clearTimer();
    this.tokenChangesSub?.unsubscribe();
    this.tokenChangesSub = undefined;
    this.crossTabEventsSub?.unsubscribe();
    this.crossTabEventsSub = undefined;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('focus', this.onWindowFocus);
  }

  rescheduleFromCurrentToken(): void {
    this.clearTimer();

    if (!this.shouldWatchSession()) {
      return;
    }

    const token = this.authService.accessToken;
    if (!token) {
      return;
    }

    const expiryMs = this.getExpiryMs(token);
    if (expiryMs == null) {
      this.tryRefreshOrLogout();
      return;
    }

    const delayMs = expiryMs - Date.now();
    if (delayMs <= 0) {
      this.tryRefreshOrLogout();
      return;
    }

    this.tokenTimerId = setTimeout(() => {
      this.tryRefreshOrLogout();
    }, delayMs);
  }

  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.rescheduleFromCurrentToken();
    }
  };

  private readonly onWindowFocus = () => {
    this.rescheduleFromCurrentToken();
  };

  private shouldWatchSession(): boolean {
    const currentUrl = this.router.url ?? '';
    return this.authService.isAuthenticated() && !currentUrl.startsWith('/auth');
  }

  private tryRefreshOrLogout(): void {
    if (!this.shouldWatchSession() || this.refreshInProgress) {
      return;
    }

    const refreshToken = this.authService.accessToken;
    this.refreshInProgress = true;
    this.authService.refreshAccessTokenCoalesced()
      .pipe(
        finalize(() => {
          this.refreshInProgress = false;
        }),
      )
      .subscribe({
        next: () => {
          this.rescheduleFromCurrentToken();
        },
        error: () => {
          if (refreshToken && this.authService.accessToken !== refreshToken) {
            this.rescheduleFromCurrentToken();
            return;
          }

          void this.authService.logout(false);
        },
      });
  }

  private getExpiryMs(token: string): number | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const payloadText = this.base64UrlDecode(parts[1]);
      const payload = JSON.parse(payloadText) as { exp?: unknown };
      if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
        return null;
      }
      return payload.exp * 1000;
    } catch {
      return null;
    }
  }

  private base64UrlDecode(value: string): string {
    const base64 = value
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return atob(padded);
  }

  private clearTimer(): void {
    if (this.tokenTimerId != null) {
      clearTimeout(this.tokenTimerId);
      this.tokenTimerId = null;
    }
  }
}
